import { Component, inject, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatButton } from '@angular/material/button';
import { Order } from '../../../classes/order.class';
import { LoaderComponent } from '../../loader/loader.component';
import { CommonDateRangeComponent } from '../../common-date-range/common-date-range.component';
import { EnumService } from '../../../services/enum.service';
import { dateRangeValidator } from '../../../validators/date-range-validator';
import { CommonDateRangeErrorsComponent } from '../../common-date-range-errors/common-date-range-errors.component';
import { AssignOrdersService } from '../../../services/assigns-orders.service';
import { IOrderDataUpdate } from '../../../typing/order.interface';
import { MessageService } from '../../../services/message.service';
import { OrdersService } from '../../../services/orders.service';
import { OrderStatusEnum, PersonStatusEnum } from '../../../enums/status.enum';
import { GroupSelectorComponent } from '../../group-selector/group-selector.component';
import { PersonSelectorComponent } from '../../person-selector/person-selector.component';
import { Params } from '@angular/router';
import { Role } from '../../../enums/role.enum';
import { setDayRange } from '../../../utils/date-format';
import { CloseButtonComponent } from '../../close-button/close-button.component';

@Component({
    selector: 'app-multi-edit-order-form',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatFormFieldModule,
        MatSelect,
        MatOption,
        MatButton,
        CommonDateRangeComponent,
        MatDialogContent,
        LoaderComponent,
        CommonDateRangeErrorsComponent,
        GroupSelectorComponent,
        PersonSelectorComponent,
        CloseButtonComponent,
        MatDialogTitle,
    ],
    templateUrl: './multi-edit-order-form.component.html',
    styleUrl: './multi-edit-order-form.component.scss',
})
export class MultiEditOrderFormComponent {
    multiEditForm: FormGroup;
    public data: { selectedOrders: Order[], workerOrdersMap: Map<number, Order[]>, currentDay: string } = inject(MAT_DIALOG_DATA);
    readonly dialogRef = inject(MatDialogRef<MultiEditOrderFormComponent>);
    protected readonly OrderStatusEnum = OrderStatusEnum;
    protected readonly PersonStatusEnum = PersonStatusEnum;
    loading = signal<boolean>(false);

    constructor(
        private fb: FormBuilder,
        protected enumService: EnumService,
        protected messageService: MessageService,
        private ordersService: OrdersService,
        private assignOrdersService: AssignOrdersService,
    ) {
        this.multiEditForm = this.fb.group(
            {
                dateFrom: [''],
                dateTo: [''],
                executorId: [''],
                groupId: [''],
                status: [''],
            },
            { validators: dateRangeValidator() }
        );

        this.multiEditForm.valueChanges.subscribe(value => {
            this.multiEditForm.setErrors(null);

            if (value.status && value.status !== OrderStatusEnum.active && !value.executorId) {
                this.multiEditForm.controls['status'].setErrors({ noExecutor: true });
            }
            if(value.dateFrom && new Date(value.dateFrom) < new Date()){
                this.multiEditForm.setErrors({pastTime: true});
            }

        });
    }

    onSubmit(): void {
        if (this.multiEditForm.invalid) {
            this.multiEditForm.markAllAsTouched();
            return;
        }

        const ordersForUpdate = this.data.selectedOrders.map((order) =>
            order.multiUpdate(this.multiEditForm.value)
        );

        if (this.multiEditForm.controls['groupId'].value && !this.multiEditForm.controls['executorId'].value) {
            ordersForUpdate.map(order => {
                order.executorId = null;
                order.status = OrderStatusEnum.active;
            });
            this.updateOrders(ordersForUpdate);
            return;
        }

        if (this.multiEditForm.controls['executorId'].value == null) {
            ordersForUpdate.map(order => order.status = OrderStatusEnum.active);
            this.updateOrders(ordersForUpdate);
            return;
        }

        // Если в форме выбран исполнитель
        const executorId: number = this.multiEditForm.controls['executorId'].value;
        const executorsOrders = this.data.workerOrdersMap.get(executorId) || [];

        // Отфильтровать заказы из workerOrdersMap, которые не входят в selectedOrders
        const unchangedOrders = executorsOrders.filter(order =>
            !this.data.selectedOrders.some(selectedOrder => selectedOrder.data.id === order.data.id)
        );

        // Обновить очередь только для selectedOrders и заказов из workerOrdersMap, которые входят в selectedOrders
        const ordersToUpdateQueue = [
            ...unchangedOrders, // Заказы, которые не изменяются
            ...this.data.selectedOrders, // Заказы, которые нужно обновить
        ];

        const concatenatedOrders = this.assignOrdersService.setQueueAfterMultiEdit(ordersToUpdateQueue);

        // Обновить заказы
        const updatedOrders = concatenatedOrders
            .map(order =>
                order.multiUpdate(this.multiEditForm.value, executorsOrders.find(oldOrder => oldOrder.data.id === order.data.id))
            );

        this.updateOrders(updatedOrders);
    }

    get executorControl(): FormControl {
        return this.multiEditForm.get('executorId') as FormControl;
    }

    get groupControl(): FormControl {
        return this.multiEditForm.get('groupId') as FormControl;
    }

    get additionalParams(): Params {
        return {
            'filter.group.id': this.groupControl.value,
            'filter.status': PersonStatusEnum.active,
            'filter.role': Role.executor,
            'filter.dateFrom': this.datePeriod
        };
    }

    changeDate(date: string | null): void {
        const [dateFrom, dateTo] = date ? date.split(',').map(this.toISOString) : [null, null];
        this.multiEditForm.patchValue({ dateFrom, dateTo });
    }

    get datePeriod(): string {
        if (this.multiEditForm.controls['dateFrom'].value && this.multiEditForm.controls['dateTo'].value) {
            return setDayRange(this.multiEditForm.controls['dateTo'].value);
        }

        return this.data.currentDay;
    }

    toISOString(date: string): string {
        return new Date(date).toISOString();
    }

    updateOrders(data: IOrderDataUpdate[]): void {
        this.ordersService
            .updateOrders(data)
            .subscribe({
                next: () => {
                    this.messageService.successMessage('Задачи успешно переназначены');
                    this.dialogRef.close();
                },
                error: () => this.messageService.errorMessage('При обновлении задач произошла ошибка'),
            })
            .add(() => this.loading.set(false));
    }
}
